import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import $ from 'jquery';
import './Player.css';

function pad(n) { return String(n).padStart(2, '0'); }

function formatDuration(ms) {
  const s = Math.floor(ms / 1000);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

export default function PlayerStatus() {
  const videoRef       = useRef(null);
  const hlsRef         = useRef(null);
  const startTimeRef   = useRef(0);
  const timerTickRef   = useRef(null);
  const metricsTickRef = useRef(null);
  const bitrateTickRef = useRef(null);
  const isLiveRef      = useRef(false);

  const [state, setState]             = useState('offline');
  const [statusText, setStatusText]   = useState('Aguardando transmissão…');
  const [liveTimer, setLiveTimer]     = useState('');
  const [metrics, setMetrics]         = useState({ res: '--', bit: '--', fps: '--', buf: '--', drop: '--' });
  const [serverStats, setServerStats] = useState({
    bytesInRate: 0, bytesOutRate: 0, totalConnections: 0, connectionCount: {}
  });
  const [muted, setMuted]   = useState(true);
  const [paused, setPaused] = useState(false);

  const getStreamUrl = () => {
    const app = localStorage.getItem('wowzaAppName') || '';
    if (window.location.hostname.includes('relaystream.io') || window.location.hostname.includes('localhost')) {
      return `https://5ad482a77183d.streamlock.net/${app}/${app}/playlist.m3u8`;
    }
    return `${window.location.protocol}//${window.location.hostname}:1935/${app}/${app}/playlist.m3u8`;
  };

  const getRtmpUrl = () => {
    const app = localStorage.getItem('wowzaAppName') || '';
    if (window.location.hostname.includes('relaystream.io') || window.location.hostname.includes('localhost')) {
      return `rtmp://saopaulo01.relaystream.io:1935/${app}`;
    }
    return `rtmp://localhost:1935/${app}`;
  };

  const rtmpUrl   = getRtmpUrl();
  const streamKey = localStorage.getItem('wowzaAppName') || '';

  const copy = (text) => navigator.clipboard.writeText(text);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const togglePause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play().catch(() => {}); setPaused(false); }
    else              { video.pause();                 setPaused(true);  }
  };

  // ── Live timer ────────────────────────────────────────────────────────────
  const startTimer = () => {
    startTimeRef.current = Date.now();
    clearInterval(timerTickRef.current);
    timerTickRef.current = setInterval(() => {
      setLiveTimer(formatDuration(Date.now() - startTimeRef.current));
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerTickRef.current);
    setLiveTimer('');
  };

  // ── HLS metrics polling ───────────────────────────────────────────────────
  const startMetrics = () => {
    clearInterval(metricsTickRef.current);
    metricsTickRef.current = setInterval(() => {
      const hls   = hlsRef.current;
      const video = videoRef.current;
      if (!hls || hls.currentLevel === -1 || !video) return;
      const level = hls.levels[hls.currentLevel];
      if (!level) return;

      const res = level.width && level.height ? `${level.width}×${level.height}` : '--';
      const bit = level.bitrate ? `${Math.round(level.bitrate / 1000)} kbps` : 'Fixo';
      const fps = level.frameRate || '--';
      let buf = '--';
      try { buf = (video.buffered.end(0) - video.currentTime).toFixed(1) + 's'; } catch (_) {}
      const drop = video.getVideoPlaybackQuality
        ? video.getVideoPlaybackQuality().droppedVideoFrames
        : '--';

      setMetrics({ res, bit, fps, buf, drop });
    }, 2000);
  };

  // ── Destroy player ────────────────────────────────────────────────────────
  const destroyPlayer = () => {
    clearInterval(metricsTickRef.current);
    stopTimer();
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    const video = videoRef.current;
    if (video) { video.removeAttribute('src'); video.load(); }
    isLiveRef.current = false;
    setMetrics({ res: '--', bit: '--', fps: '--', buf: '--', drop: '--' });
  };

  // ── Init HLS player ───────────────────────────────────────────────────────
  // Use a ref so the interval callback always gets the latest version.
  const initPlayerRef = useRef(null);
  initPlayerRef.current = () => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }

    const video = videoRef.current;
    if (!video) return;

    if (!Hls.isSupported()) {
      video.src = getStreamUrl();
      video.play().catch(() => {});
      return;
    }

    const hls = new Hls({ lowLatencyMode: true, backBufferLength: 0 });
    hlsRef.current = hls;
    hls.loadSource(getStreamUrl());
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch(() => {});
    });

    hls.on(Hls.Events.FRAG_LOADED, () => {
      if (!isLiveRef.current) {
        isLiveRef.current = true;
        setState('online');
        setStatusText('AO VIVO');
        startTimer();
        startMetrics();
      }
    });

    hls.on(Hls.Events.ERROR, (_, data) => {
      if (data.fatal) {
        hls.destroy();
        hlsRef.current = null;
        isLiveRef.current = false;
        // Don't change state here — the bitrate poll drives state
      }
    });
  };

  // ── Bitrate API — master stream switch ───────────────────────────────────
  // Use a ref so the setInterval callback always calls the latest version.
  const checkBitrateRef = useRef(null);
  checkBitrateRef.current = () => {
    $.ajax({
      url: '/api/v1/bitrate',
      method: 'GET',
      success(resp) {
        const data = (resp && resp.response) ? resp.response : resp || {};
        setServerStats({
          bytesInRate:      data.bytesInRate      || 0,
          bytesOutRate:     data.bytesOutRate      || 0,
          totalConnections: data.totalConnections  || 0,
          connectionCount:  data.connectionCount   || {}
        });

        if (data.bytesInRate > 0) {
          // Stream is active — init player if not already running
          if (!hlsRef.current && !isLiveRef.current) {
            setState('reconnect');
            setStatusText('Conectando…');
            initPlayerRef.current();
          }
        } else {
          // Stream is offline
          if (isLiveRef.current || hlsRef.current) destroyPlayer();
          setState('offline');
          setStatusText('Aguardando transmissão…');
        }
      },
      error() {}
    });
  };

  useEffect(() => {
    checkBitrateRef.current();
    bitrateTickRef.current = setInterval(() => checkBitrateRef.current(), 10000);

    return () => {
      clearInterval(bitrateTickRef.current);
      clearInterval(timerTickRef.current);
      clearInterval(metricsTickRef.current);
      if (hlsRef.current) hlsRef.current.destroy();
    };
  }, []);

  const inRateKbps  = serverStats.bytesInRate  > 0 ? `${Math.round(serverStats.bytesInRate  / 1000)} kbps` : '--';
  const outRateKbps = serverStats.bytesOutRate > 0 ? `${Math.round(serverStats.bytesOutRate / 1000)} kbps` : '--';
  const cc          = serverStats.connectionCount;

  const showOverlay = state !== 'online';
  const dotClass    = `rs-mini-dot ${state}`;

  return (
    <div className="rs-player-area">
      {/* Video + overlays */}
      <div className="rs-video-wrap">
        {/* Status badge */}
        <div className="rs-mini-status">
          <div className={dotClass} />
          <span>{state.toUpperCase()}</span>
          {state === 'online' && liveTimer && (
            <span className="rs-live-timer">{liveTimer}</span>
          )}
        </div>

        {/* Offline / connecting overlay */}
        {showOverlay && (
          <div className="rs-status-overlay">
            <span>{statusText}</span>
            {state === 'reconnect' && (
              <div className="rs-reconnect-dots">
                <span /><span /><span />
              </div>
            )}
          </div>
        )}

        <video
          ref={videoRef}
          id="playerStatusVideo"
          autoPlay
          muted
          playsInline
        />

        {/* Player controls */}
        <div className="rs-player-controls">
          <button
            className={`rs-ctrl-btn${muted ? ' muted' : ''}`}
            onClick={toggleMute}
            title={muted ? 'Unmute audio' : 'Mute audio'}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.63 3.63a.996.996 0 0 0 0 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 1 0 1.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.2-1.22.84v.08c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0 0 14 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            )}
            <span>{muted ? 'Unmute' : 'Mute'}</span>
          </button>

          <button
            className={`rs-ctrl-btn${paused ? ' paused' : ''}`}
            onClick={togglePause}
            title={paused ? 'Resume video' : 'Pause video'}
          >
            {paused ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            )}
            <span>{paused ? 'Resume' : 'Pause'}</span>
          </button>
        </div>

        {/* HLS metrics bar */}
        <div className="rs-metrics-bar">
          <div className="rs-metric"><span>Resolução</span>{metrics.res}</div>
          <div className="rs-metric"><span>Bitrate</span>{metrics.bit}</div>
          <div className="rs-metric"><span>FPS</span>{metrics.fps}</div>
          <div className="rs-metric"><span>Buffer</span>{metrics.buf}</div>
          <div className="rs-metric"><span>Dropped</span>{metrics.drop}</div>
        </div>
      </div>

      {/* Server stats from bitrate API */}
      {/* <div className="rs-server-stats">
        <div className="rs-stat-item">
          <span>Entrada</span>
          <strong>{inRateKbps}</strong>
        </div>
        <div className="rs-stat-item">
          <span>Saída</span>
          <strong>{outRateKbps}</strong>
        </div>
        <div className="rs-stat-item">
          <span>Conexões</span>
          <strong>{serverStats.totalConnections}</strong>
        </div>
        <div className="rs-stat-item">
          <span>RTMP</span>
          <strong>{cc.RTMP || 0}</strong>
        </div>
        <div className="rs-stat-item">
          <span>HLS</span>
          <strong>{cc.CUPERTINO || 0}</strong>
        </div>
      </div> */}

      {/* RTMP settings */}
      <div className="rs-rtmp-box">
        <strong>Configurações RTMP</strong>
        <div className="rs-rtmp-row">
          <input type="text" readOnly value={rtmpUrl} />
          <button className="rs-copy-btn" onClick={() => copy(rtmpUrl)}>Copiar</button>
        </div>
        <div className="rs-rtmp-row">
          <input type="text" readOnly value={streamKey} />
          <button className="rs-copy-btn" onClick={() => copy(streamKey)}>Copiar</button>
        </div>
      </div>
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/components/DashboardPage/Dashboard/Video/Player/Player.js